import {useMemo, useRef} from 'react';

import {useHasReportRuns} from '../../../state/runs/context';
import {
  type RunHistoryKeyInfo,
  type RunHistoryKeyType,
} from '../../../types/run';
import {type RunsLinePlotConfig} from '../types';
import {getBaseXYChoices, withSelectedXAxisChoice} from '../utils/axis';

const EMPTY_METRICS: string[] = [];

function stringArraysEqual(left: string[], right: string[]) {
  if (left === right) {
    return true;
  }
  if (left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
}

function useStableStringArray(value: string[]) {
  const ref = useRef(value);
  if (!stringArraysEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

export const useXYChoices = ({
  config,
  keyInfo,
  keyTypes,
  isSingleRun,
  xAxis,
}: {
  xAxis: string;
  config: RunsLinePlotConfig;
  keyInfo: RunHistoryKeyInfo;
  keyTypes: {[key: string]: RunHistoryKeyType};
  isSingleRun: boolean;
}) => {
  const hasRunsReport = useHasReportRuns();
  const configMetrics = useStableStringArray(config.metrics ?? EMPTY_METRICS);
  const choiceKeyInfo = useMemo(
    () => ({
      keys: keyInfo.keys,
      sortedKeys: keyInfo.sortedKeys,
    }),
    [keyInfo.keys, keyInfo.sortedKeys]
  );

  // Scan the large key list once for base x/y choices, then apply selected
  // xAxis separately so x-axis edits don't rebuild y choices.
  const {baseXAxisChoices, yAxisChoices} = useMemo(() => {
    return getBaseXYChoices({
      configMetrics,
      keyInfo: choiceKeyInfo,
      keyTypes,
      isHistogramAllowed: isSingleRun || hasRunsReport,
    });
  }, [choiceKeyInfo, configMetrics, keyTypes, isSingleRun, hasRunsReport]);

  const xAxisChoices = useMemo(() => {
    return withSelectedXAxisChoice(baseXAxisChoices, xAxis);
  }, [baseXAxisChoices, xAxis]);

  return {
    xAxisChoices,
    yAxisChoices,
  };
};
